import { useCallback } from 'react';
import { useCoreMutate } from '$api-clients/hooks';

/**
 * Revalidates every interaction key `useInteraction` might be reading: the
 * type-specific endpoint plus the generic one it falls back to.
 *
 * Revalidates in place (`populateCache: false`) so the refresh doesn't blank
 * the cache and flash a loading state.
 */
export function useRevalidateInteraction() {
  const mutate = useCoreMutate();

  return useCallback(
    async (interactionId: string, interactionType: string) => {
      const typed = () => {
        switch (interactionType) {
          case 'listing':
            return mutate(
              [
                '/listings/{listingId}',
                { params: { path: { listingId: interactionId } } },
              ],
              undefined,
              { revalidate: true, populateCache: false },
            );
          case 'offer':
            return mutate(
              [
                '/offers/{offerId}',
                { params: { path: { offerId: interactionId } } },
              ],
              undefined,
              { revalidate: true, populateCache: false },
            );
          case 'referral':
            return mutate(
              [
                '/referrals/{referralId}',
                { params: { path: { referralId: interactionId } } },
              ],
              undefined,
              { revalidate: true, populateCache: false },
            );
          case 'clientGroup':
            return mutate(
              [
                '/client-groups/{clientGroupId}',
                { params: { path: { clientGroupId: interactionId } } },
              ],
              undefined,
              { revalidate: true, populateCache: false },
            );
          default:
            return undefined;
        }
      };

      await Promise.all([
        typed(),
        mutate(
          [
            '/interactions/{interactionId}',
            { params: { path: { interactionId } } },
          ],
          undefined,
          { revalidate: true, populateCache: false },
        ),
      ]);
    },
    [mutate],
  );
}
